/**
 * Utility functions for parsing location data from map URLs
 */

/**
 * Parse manual coordinate input (lat, lng format)
 * @param {string} input - Coordinate string like "37.7749, -122.4194"
 * @returns {object|null} - Location data or null if invalid
 */
export const parseManualCoordinates = input => {
  if (!input || typeof input !== 'string') {
    return null;
  }

  const trimmed = input.trim();
  const coords = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);

  if (coords && coords.length >= 3) {
    const lat = parseFloat(coords[1]);
    const lng = parseFloat(coords[2]);

    if (
      !isNaN(lat) &&
      !isNaN(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    ) {
      return {
        latitude: coords[1],
        longitude: coords[2],
        name: 'Manual Location',
        address: `${coords[1]}, ${coords[2]}`,
      };
    }
  }

  return null;
};

/**
 * Parse location data from various map service URLs
 * Supports:
 * - Google Maps: https://www.google.com/maps?q=lat,lng or https://maps.app.goo.gl/xxx
 * - Google Maps Place: https://www.google.com/maps/place/Name/@lat,lng,zoom
 * - Apple Maps: https://maps.apple.com/?q=lat,lng
 * - OpenStreetMap: https://www.openstreetmap.org/?mlat=lat&mlon=lng
 * - Manual coordinates: "37.7749, -122.4194"
 *
 * @param {string} url - The map URL or coordinates to parse
 * @returns {object|null} - Location data {latitude, longitude, name, address} or null if invalid
 */
export const parseLocationFromUrl = url => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const trimmed = url.trim();

  // First try to parse as manual coordinates (lat, lng)
  const manualCoords = parseManualCoordinates(trimmed);
  if (manualCoords) {
    return manualCoords;
  }

  try {
    const urlObj = new URL(trimmed);

    // Google Maps formats
    if (
      urlObj.hostname.includes('google.com') ||
      urlObj.hostname.includes('goo.gl')
    ) {
      return parseGoogleMapsUrl(urlObj, trimmed);
    }

    // Apple Maps
    if (urlObj.hostname.includes('apple.com')) {
      return parseAppleMapsUrl(urlObj);
    }

    // OpenStreetMap
    if (urlObj.hostname.includes('openstreetmap.org')) {
      return parseOpenStreetMapUrl(urlObj);
    }

    return null;
  } catch (error) {
    // If URL parsing fails, it might be manual coordinates or invalid
    // We already tried manual coordinates above, so return null
    return null;
  }
};

/**
 * Parse Google Maps URL
 * Uses multiple strategies to extract coordinates from various Google Maps URL formats
 */
const parseGoogleMapsUrl = (urlObj, originalUrl) => {
  // Strategy 1: Match @latitude,longitude pattern (most common in share links)
  // Regex: @ followed by lat,lng with optional decimal points
  // Example: https://www.google.com/maps/place/Name/@37.7749,-122.4194,15z
  const atMatch = originalUrl.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (atMatch && atMatch.length >= 3) {
    const lat = parseFloat(atMatch[1]);
    const lng = parseFloat(atMatch[2]);
    if (isValidCoordinate(lat, lng)) {
      const name = extractPlaceName(originalUrl);
      return {
        latitude: atMatch[1],
        longitude: atMatch[2],
        name: name || 'Shared Location',
        address: name || 'Google Maps Location',
      };
    }
  }

  // Strategy 2: Query parameter ?q=lat,lng
  const q = urlObj.searchParams.get('q');
  if (q) {
    const coords = q.match(/^(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)$/);
    if (coords && coords.length >= 3) {
      const lat = parseFloat(coords[1]);
      const lng = parseFloat(coords[2]);
      if (isValidCoordinate(lat, lng)) {
        return {
          latitude: coords[1],
          longitude: coords[2],
          name: 'Search Location',
          address: q,
        };
      }
    }
  }

  // Strategy 3: ll= parameter (used in some Google Maps URLs)
  const ll = urlObj.searchParams.get('ll');
  if (ll) {
    const coords = ll.match(/^(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)$/);
    if (coords && coords.length >= 3) {
      const lat = parseFloat(coords[1]);
      const lng = parseFloat(coords[2]);
      if (isValidCoordinate(lat, lng)) {
        return {
          latitude: coords[1],
          longitude: coords[2],
          name: 'Map Location',
          address: 'Google Maps Location',
        };
      }
    }
  }

  // Strategy 4: /place/ or /search/ with coordinates
  const placeMatch = originalUrl.match(
    /\/(place|search)\/[^/]*\/data=[^@]*@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/
  );
  if (placeMatch && placeMatch.length >= 4) {
    const lat = parseFloat(placeMatch[2]);
    const lng = parseFloat(placeMatch[3]);
    if (isValidCoordinate(lat, lng)) {
      return {
        latitude: placeMatch[2],
        longitude: placeMatch[3],
        name: 'Map Location',
        address: 'Google Maps Location',
      };
    }
  }

  // Strategy 5: Any coordinate pair in the URL (last resort)
  // This catches most formats but is less precise
  const allMatches = originalUrl.matchAll(
    /(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/g
  );
  for (const match of allMatches) {
    if (match && match.length >= 3) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      if (isValidCoordinate(lat, lng)) {
        return {
          latitude: match[1],
          longitude: match[2],
          name: 'Map Location',
          address: 'Google Maps Location',
        };
      }
    }
  }

  return null;
};

/**
 * Helper to validate coordinate ranges
 */
const isValidCoordinate = (lat, lng) => {
  return (
    !isNaN(lat) &&
    !isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
};

/**
 * Extract place name from Google Maps URL
 */
const extractPlaceName = url => {
  // Try to extract from /place/Name/ pattern
  const placeMatch = url.match(/\/place\/([^/@]+)/);
  if (placeMatch) {
    return decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
  }
  return '';
};

/**
 * Parse Apple Maps URL
 */
const parseAppleMapsUrl = urlObj => {
  // Apple Maps uses ?q=latitude,longitude or ?ll=latitude,longitude
  const q = urlObj.searchParams.get('q');
  const ll = urlObj.searchParams.get('ll');

  const coordString = q || ll;
  if (coordString) {
    const coords = coordString.split(',');
    if (coords.length === 2) {
      return {
        latitude: coords[0].trim(),
        longitude: coords[1].trim(),
        name: urlObj.searchParams.get('address') || 'Location',
        address: urlObj.searchParams.get('address') || '',
      };
    }
  }

  return null;
};

/**
 * Parse OpenStreetMap URL
 */
const parseOpenStreetMapUrl = urlObj => {
  const mlat = urlObj.searchParams.get('mlat');
  const mlon = urlObj.searchParams.get('mlon');

  if (mlat && mlon) {
    return {
      latitude: mlat,
      longitude: mlon,
      name: 'Location',
      address: '',
    };
  }

  return null;
};

/**
 * Validate location data
 * @param {object} location - Location data object
 * @returns {boolean} - True if valid
 */
export const isValidLocation = location => {
  if (!location || typeof location !== 'object') {
    return false;
  }

  const { latitude, longitude } = location;

  // Check if latitude and longitude are present and valid numbers
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);

  if (isNaN(lat) || isNaN(lng)) {
    return false;
  }

  // Validate ranges: latitude -90 to 90, longitude -180 to 180
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return false;
  }

  return true;
};

/**
 * Format location data for display
 * @param {object} location - Location data object
 * @returns {string} - Formatted location string
 */
export const formatLocationDisplay = location => {
  if (!location) {
    return '';
  }

  const parts = [];

  if (location.name) {
    parts.push(location.name);
  }

  if (location.address) {
    parts.push(location.address);
  }

  if (location.latitude && location.longitude) {
    parts.push(`(${location.latitude}, ${location.longitude})`);
  }

  return parts.join(' - ');
};

/**
 * Create a Google Maps URL from location data
 * @param {object} location - Location data object
 * @returns {string} - Google Maps URL
 */
export const createMapsUrl = location => {
  if (!location || !location.latitude || !location.longitude) {
    return '';
  }

  const { latitude, longitude, name } = location;
  const query = name || `${latitude},${longitude}`;

  return `https://www.google.com/maps?q=${encodeURIComponent(query)}@${latitude},${longitude}`;
};
